import { useCallback } from 'react'
import { isArrayBuffer, isString } from 'lodash'
import { IFileCreate } from 'types'
import { Maybe } from 'types/graphqlTypes'
import { useGlobalToast } from 'transfix-ui'

/**
 * @param {string} dataUri base64 string in format: `data:[<media type>][;base64],<data>`
 * @returns The base64 encoded data as a Blob
 */
const dataUriToBlob = (dataUri: string): Promise<Blob> =>
  new Promise<Blob>((resolve, reject) => {
    try {
      const [identifier, data] = dataUri.split(',')
      const mediaType = identifier.split(':')[1].split(';')[0]
      const binary = atob(data)
      // convert binary string to array and map characters to their Unicode value
      const array = binary.split('').map(char => char.charCodeAt(0))
      const blob = new Blob([new Uint8Array(array)], { type: mediaType })
      resolve(blob)
    } catch (err) {
      reject(err)
    }
  })

const arrayBufferToBlob = (arrayBuffer: ArrayBuffer): Blob => new Blob([arrayBuffer])

export const useLaneAgreementPreview = () => {
  const { openToast } = useGlobalToast()

  const openLaneAgreementPreview = useCallback(
    async (laneAgreement: Maybe<IFileCreate>) => {
      try {
        const { file } = laneAgreement ?? {}
        let blob: Blob

        if (isString(file)) {
          blob = await dataUriToBlob(file)
        } else if (isArrayBuffer(file)) {
          blob = arrayBufferToBlob(file)
        } else {
          throw new Error('Invalid file.')
        }

        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
      } catch (err) {
        openToast({
          toastMessage: `Failed to open Lane Agreement preview: ${err?.message}`,
          toastType: 'alert',
        })
      }
    },
    [openToast]
  )

  return {
    openLaneAgreementPreview,
  }
}
